import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, MapPin } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { CONTACT_EMAIL } from "../data/site";
import { canonicalUrl } from "../data/seo";
import { SOCIAL_ICONS, SOCIAL_ORDER } from "../data/socialIcons";

export default function Contact() {
  usePageMeta({
    title: "Contact Us | Digsby Early Learning",
    description:
      "Get in touch with Digsby Early Learning, questions about our books, bulk orders, or partnerships welcome.",
    canonical: canonicalUrl("/contact"),
  });

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [socials, setSocials] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/social`)
      .then(({ data }) => setSocials(Array.isArray(data) ? data : []))
      .catch(() => setSocials([]));
  }, []);

  const orderedSocials = SOCIAL_ORDER
    .map((key) => socials.find((s) => s.platform === key))
    .filter(Boolean);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
        form
      );
      toast.success(data.message || "Message sent!", {
        description: "Thanks for reaching out, we'll get back to you soon.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFFBF2] min-h-screen">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 text-sm font-bold text-[#FF0000] uppercase tracking-wider">
            <Mail size={16} /> Say Hello
          </span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl mt-3 text-black">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-slate-700">
            Questions about the books, bulk orders for your school, or author appearances?
            We'd love to hear from you.
          </p>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.4fr_1fr] items-start">
          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-white rounded-[2rem] border-2 border-black/10 p-6 sm:p-10 shadow-sm"
          >
            <div className="grid gap-5">
              <div>
                <label htmlFor="name" className="block font-heading font-bold mb-2">Name</label>
                <input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name"
                  placeholder="Your name"
                  className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] focus:ring-2 focus:ring-[#0000FF]/20"
                />
              </div>
              <div>
                <label htmlFor="email" className="block font-heading font-bold mb-2">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email"
                  placeholder="you@example.com"
                  className="w-full min-h-[52px] rounded-2xl border-2 border-black/15 px-5 font-medium outline-none focus:border-[#0000FF] focus:ring-2 focus:ring-[#0000FF]/20"
                />
              </div>
              <div>
                <label htmlFor="message" className="block font-heading font-bold mb-2">Message</label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message"
                  placeholder="How can we help?"
                  className="w-full rounded-2xl border-2 border-black/15 px-5 py-4 font-medium outline-none focus:border-[#0000FF] focus:ring-2 focus:ring-[#0000FF]/20 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center justify-center gap-2 min-h-[54px] rounded-full bg-[#FF0000] text-white font-bold text-lg px-8 hover:bg-[#d60000] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Message"} <Send size={18} />
              </button>
            </div>
          </form>

          {/* Info */}
          <div className="space-y-5">
            <div className="bg-[#0000FF] text-white rounded-[2rem] p-8">
              <Mail size={32} className="text-[#FBEF14] mb-4" />
              <h3 className="font-heading font-bold text-xl mb-2">Email us</h3>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="font-medium underline decoration-[#FBEF14] underline-offset-4 break-all"
              >
                {CONTACT_EMAIL}
              </a>
            </div>

            <div className="bg-white rounded-[2rem] border-2 border-black/10 p-8" data-testid="contact-socials-card">
              <h3 className="font-heading font-bold text-xl mb-4">Follow us</h3>
              {orderedSocials.length > 0 ? (
                <div className="flex gap-3 flex-wrap" data-testid="contact-socials">
                  {orderedSocials.map((s) => {
                    const brand = SOCIAL_ICONS[s.platform] || {};
                    const Icon = brand.Icon;
                    return (
                      <a
                        key={s.platform}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-social-${s.platform}`}
                        aria-label={s.name}
                        title={s.name}
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FFFBF2] border-2 border-black/10 text-slate-700 hover:text-white hover:-translate-y-0.5 transition-all duration-200"
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = brand.color || "#0000FF"; e.currentTarget.style.borderColor = brand.color || "#0000FF"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = ""; }}
                      >
                        {Icon && <Icon size={22} />}
                      </a>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-600 font-medium">
                  Follow us on <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0000FF] underline">email</a> for the latest updates, social channels coming soon.
                </p>
              )}
            </div>

            <div className="bg-[#FBEF14] rounded-[2rem] border-2 border-black p-8">
              <MapPin size={30} className="text-black mb-3" />
              <h3 className="font-heading font-bold text-xl text-black mb-1">Where to buy</h3>
              <p className="text-black/80 font-medium">
                All Digsby Early Learning books are available on Amazon, which handles
                payment, printing, and shipping.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
